import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, EmptyState, LoadingSkeleton } from '@nearrme/ui-web';
import { listBrands, listConsumers, listOrders, listProducts } from '../api';
import { listContactMessages } from '../../api/contactMessages';

function Detail({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
      <div className="mt-1 text-sm font-medium text-slate-900 dark:text-slate-100">{value ?? '-'}</div>
    </div>
  );
}

function Shell({ title, backTo, children }: { title: string; backTo: string; children: ReactNode }) {
  return (
    <div className="space-y-4">
      <Link to={backTo} className="text-sm font-semibold text-brand-700 hover:underline dark:text-brand-300">
        Back
      </Link>
      <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">{title}</h1>
      {children}
    </div>
  );
}

export function AdminOrderDetailPage() {
  const { orderId = '' } = useParams<{ orderId: string }>();
  const orders = useQuery({ queryKey: ['admin', 'orders', '__all'], queryFn: () => listOrders('__all') });
  const order = orders.data?.find((item) => String(item.consumerOrderId) === orderId);

  if (orders.isLoading) return <LoadingSkeleton className="h-48 w-full" />;
  if (!order) return <EmptyState title="Order not found" description="The order is not available in the current scope." />;

  return (
    <Shell title={`Order #${order.consumerOrderId}`} backTo="/admin/orders">
      <Card padded>
        <div className="grid gap-4 md:grid-cols-3">
          <Detail label="Status" value={order.orderStatus} />
          <Detail label="Channel" value={order.orderChannel} />
          <Detail label="Service method" value={order.serviceMethod} />
          <Detail label="Product" value={order.productName} />
          <Detail label="Brand" value={order.brandName} />
          <Detail label="Quantity" value={order.quantity} />
          <Detail label="Store" value={order.storeName ?? 'Online fulfilment'} />
          <Detail label="Company" value={order.companyCode} />
          <Detail label="Customer" value={order.consumerName} />
          <Detail label="Customer email" value={order.consumerEmail} />
          <Detail label="Total" value={`Rs. ${order.totalInr.toLocaleString('en-IN')}`} />
          <Detail label="Warranty" value={order.warrantyRecordId ?? 'Pending'} />
        </div>
      </Card>
    </Shell>
  );
}

export function AdminProductDetailPage() {
  const { productId = '' } = useParams<{ productId: string }>();
  const products = useQuery({ queryKey: ['admin', 'products', '__all'], queryFn: () => listProducts('__all') });
  const product = products.data?.find((item) => String(item.productId) === productId);

  if (products.isLoading) return <LoadingSkeleton className="h-48 w-full" />;
  if (!product) return <EmptyState title="Product not found" description="The product is not available in the current scope." />;

  return (
    <Shell title={product.productName ?? `Product ${product.productId}`} backTo="/admin/products">
      <Card padded>
        <div className="grid gap-4 md:grid-cols-3">
          <Detail label="Product ID" value={product.productId} />
          <Detail label="Company" value={product.companyCode} />
          <Detail label="Brand" value={product.brandName} />
          <Detail label="Category" value={product.productCategoryName} />
          <Detail label="Default price" value={product.defaultPrice ?? '-'} />
          <Detail label="Sale price" value={product.salesPrice ?? '-'} />
          <Detail label="Availability" value={product.availabilityStatus ?? (product.inStock ? 'Available' : 'Out of Stock')} />
          <Detail label="Quantity" value={product.availableQuantity ?? 0} />
          <Detail label="Comment" value={product.availabilityComment} />
        </div>
      </Card>
    </Shell>
  );
}

export function AdminBrandDetailPage() {
  const { brandId = '' } = useParams<{ brandId: string }>();
  const brands = useQuery({ queryKey: ['admin', 'brands', 'all'], queryFn: () => listBrands('') });
  const brand = brands.data?.find((item) => String(item.brandId) === brandId);

  if (brands.isLoading) return <LoadingSkeleton className="h-48 w-full" />;
  if (!brand) return <EmptyState title="Brand not found" description="The brand is not available in the current scope." />;

  return (
    <Shell title={brand.brandName ?? `Brand ${brand.brandId}`} backTo="/admin/brands">
      <Card padded>
        <div className="grid gap-4 md:grid-cols-3">
          <Detail label="Brand ID" value={brand.brandId} />
          <Detail label="Company" value={brand.companyCode} />
          <Detail label="Category" value={brand.category} />
          <Detail label="Description" value={brand.description} />
          <Detail label="Image" value={brand.imageName} />
          <Detail label="Status" value={brand.isActive ? 'Active' : 'Inactive'} />
        </div>
      </Card>
    </Shell>
  );
}

export function AdminConsumerDetailPage() {
  const { consumerId = '' } = useParams<{ consumerId: string }>();
  const consumers = useQuery({ queryKey: ['admin', 'consumers', '__all'], queryFn: () => listConsumers('__all') });
  const consumer = consumers.data?.find((item) => String(item.consumerId) === consumerId);

  if (consumers.isLoading) return <LoadingSkeleton className="h-48 w-full" />;
  if (!consumer) return <EmptyState title="Consumer not found" description="The consumer is not available in the current scope." />;

  return (
    <Shell title={consumer.name} backTo="/admin/consumers">
      <Card padded>
        <div className="grid gap-4 md:grid-cols-3">
          <Detail label="Consumer ID" value={consumer.consumerId} />
          <Detail label="Email" value={consumer.email} />
          <Detail label="Phone" value={consumer.phoneNumber} />
          <Detail label="Orders" value={consumer.orderCount} />
          <Detail label="Spend" value={`Rs. ${consumer.totalSpendInr.toLocaleString('en-IN')}`} />
          <Detail label="Created" value={new Date(consumer.createdDate).toLocaleString()} />
        </div>
      </Card>
      <Card padded>
        <h2 className="text-lg font-semibold text-slate-950 dark:text-slate-50">Orders</h2>
        <div className="mt-3 grid gap-3">
          {consumer.orders.map((order) => (
            <Link key={order.consumerOrderId} to={`/admin/orders/${order.consumerOrderId}`} className="rounded-lg border border-slate-200 p-3 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-900">
              <div className="font-semibold text-slate-900 dark:text-slate-100">{order.productName}</div>
              <div className="text-xs text-slate-500">{order.companyCode} | Rs. {order.totalInr.toLocaleString('en-IN')}</div>
            </Link>
          ))}
        </div>
      </Card>
    </Shell>
  );
}

export function AdminContactMessageDetailPage() {
  const { messageId = '' } = useParams<{ messageId: string }>();
  const messages = useQuery({ queryKey: ['admin', 'contact-messages'], queryFn: () => listContactMessages() });
  const message = messages.data?.find((item) => String(item.contactMessageId) === messageId);

  if (messages.isLoading) return <LoadingSkeleton className="h-48 w-full" />;
  if (!message) return <EmptyState title="Message not found" description="The message is not available in the current scope." />;

  return (
    <Shell title={`${message.firstName} ${message.lastName}`} backTo="/admin/contact-messages">
      <Card padded>
        <div className="grid gap-4 md:grid-cols-3">
          <Detail label="Status" value={message.status} />
          <Detail label="Email" value={message.email} />
          <Detail label="Phone" value={message.phone} />
          <Detail label="Source" value={message.sourcePage} />
          <Detail label="Created" value={new Date(message.createdAtUtc).toLocaleString()} />
        </div>
        <div className="mt-6">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Message</div>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700 dark:text-slate-200">{message.message}</p>
        </div>
      </Card>
    </Shell>
  );
}
